import { createSelector } from '@reduxjs/toolkit';
import { AppState as State } from '@ducks/index';
import { OptionItem } from '@uiParts/selectBox';
import { BUSINESS_TYPE_LIST_LINKED_INSPECTION_DIVISION } from '@utils/constants';

export const getRefreshId = (state: State) => state.common.refresh;

export const checkIsLoading = (state: State) => state.common.isLoading;

export const checkIsProcessing = (state: State) => state.common.isProcessing;

export const getKeywordRefreshId = (state: State) => state.common.keywordRefresh;
/**
 * @summary タグ選択用の建物リスト取得
 * @description [サーバー側処理]お気に入りを優先的に上位に並べ、次にidを昇順 に並べられたものがリストとして返ってくる
 */
export const getPropertyList = (state: State) => {
  return state.common.propertyList;
};

export const getPropertyListFilteredByTagIds = (state: State) => {
  return state.common.propertyListFilteredByTagIds;
};

export const getSelectedPropertyId = (state: State) => state.common.selectedPropertyId;
export const getSelectedProperty = (state: State) =>
  state.common.propertyList.find((property) => property.id === state.common.selectedPropertyId) ??
  null;

export const getSelectedTagIds = (state: State) => state.common.selectedTagIds;

export const getProfile = (state: State) => state.common.profile;

export const getUserId = (state: State) => state.common.profile?.userId ?? null;

export const checkIsSystemAdmin = (state: State) => state.common.profile?.isSystemAdmin ?? false;

export const checkIsPropertyAdmin = (state: State) =>
  state.common.profile?.properties.filter((v) => v.roleId === 2).map((w) => w.id) ?? [];

export const checkIsPropertyStaff = (state: State) =>
  state.common.profile?.properties.filter((v) => v.roleId === 1).map((w) => w.id) ?? [];

export const checkIsPropertyManager = (id: number) =>
  createSelector(
    [
      (state: State) =>
        state.common.profile?.properties.filter((v) => v.roleId === 2).map((w) => w.id) ?? [],
    ],
    (properties: number[]) => properties.includes(id),
  );

// 建物スタッフかどうかチェック
export const checkIsPropertyStaffUser = (propertyId: number) =>
  createSelector(
    [
      (state: State) =>
        state.common.profile?.properties.filter((v) => v.roleId === 1).map((w) => w.id) ?? [],
    ],
    (properties: number[]) => properties.includes(propertyId),
  );

// 建物担当者(建物スタッフまたは建物管理者のいずれか)かどうかチェック
export const checkIsPropertyAssigneeUser = (propertyId: number) =>
  createSelector(
    [
      (state: State) =>
        state.common.profile?.properties
          .filter((v) => v.roleId === 1 || v.roleId === 2) // roleIdが1スタッフまたは2管理者のプロパティを抽出
          .map((w) => w.id) ?? [],
    ],
    (properties: number[]) => properties.includes(propertyId),
  );

/**
 * @summary 定期業務区分毎にコンボボックスで使用できる業務種別の選択肢を返却する
 * @param {State} state
 * @returns {OptionItem[][]}
 */
export const getBusinessTypeOptions: (state: State) => OptionItem[][] = (state: State) => {
  const businessTypeOptions: OptionItem[][] = [];
  Object.keys(BUSINESS_TYPE_LIST_LINKED_INSPECTION_DIVISION).forEach((businessType) => {
    businessTypeOptions[Number(businessType)] = state.common.equipmentMst.equipment_type_list
      .filter((equipment_type) =>
        BUSINESS_TYPE_LIST_LINKED_INSPECTION_DIVISION[businessType].includes(
          equipment_type.mst_equipment_type_id,
        ),
      )
      .map((equipment_type) => {
        return {
          title: equipment_type.mst_equipment_type_name,
          value: equipment_type.mst_equipment_type_id,
        };
      });
  });
  return businessTypeOptions;
};

/**
 * @summary 機器(業務)種別のマスタ情報からコンボボックスで使用できる選択肢を返却する
 * @param {State} state
 * @returns {OptionItem[]}
 */
export const getEquipmentTypeOptions: (state: State) => OptionItem[] = (state: State) =>
  state.common.equipmentMst.equipment_type_list.map((equipment_type) => {
    return {
      title: equipment_type.mst_equipment_type_name,
      value: equipment_type.mst_equipment_type_id,
    };
  });

/**
 * @summary 機器(業務)科目のマスタ情報から機器(業務)種別毎のコンボボックスで使用できる選択肢を返却する
 * @param {State} state
 * @returns {OptionItem[][]}
 */
export const getEquipmentSubjectOptions: (state: State) => OptionItem[][] = (state: State) => {
  const equipmentSubjectOptions: OptionItem[][] = [];
  state.common.equipmentMst.equipment_type_list.forEach((equipment_type) => {
    equipmentSubjectOptions[equipment_type.mst_equipment_type_id] =
      state.common.equipmentMst.equipment_subject_list
        .filter(
          (equipment_subject) =>
            equipment_type.mst_equipment_type_id === equipment_subject.mst_equipment_type_id,
        )
        .map((equipment_subject) => {
          return {
            title: equipment_subject.mst_equipment_subject_name,
            value: equipment_subject.mst_equipment_subject_id,
          };
        });
  });
  return equipmentSubjectOptions;
};
export const getRuntimeError = (state: State) => state.common.runtimeError;

export const checkIsPublic = (state: State) => state.common.isPublic;
